import React, { useState } from 'react';
import { X, Mail, Send, CheckCircle2, MessageSquare, LifeBuoy, Shield, Loader2 } from 'lucide-react';

interface ContactSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
  userName?: string;
}

export const ContactSupportModal: React.FC<ContactSupportModalProps> = ({
  isOpen,
  onClose,
  userEmail = '',
  userName = '',
}) => {
  const [name, setName] = useState(userName);
  const [email, setEmail] = useState(userEmail);
  const [category, setCategory] = useState<'reset_password' | 'oauth_domain' | 'email_confirmation' | 'account_issue' | 'other'>('reset_password');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !message.trim()) return;

    setIsSending(true);
    setTimeout(() => {
      setIsSending(false);
      setSubmitted(true);
    }, 700);
  };

  return (
    <div className="fixed inset-0 z-50 bg-indigo-950/85 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-indigo-900/90 text-white rounded-[2.5rem] border border-white/15 shadow-2xl w-full max-w-lg overflow-hidden p-6 space-y-5 relative my-8">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-indigo-200 hover:text-white rounded-full bg-indigo-950/80 border border-white/10 transition"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center space-y-1.5">
          <div className="w-12 h-12 rounded-2xl bg-lime-400 text-black mx-auto flex items-center justify-center font-black text-xl shadow-lg shadow-lime-400/20">
            <LifeBuoy className="w-6 h-6 stroke-[2.5]" />
          </div>
          <h2 className="text-2xl font-black italic uppercase tracking-tight text-white pt-1">
            Playground League Support
          </h2>
          <p className="text-xs text-indigo-200/80 font-semibold">
            Direct communication desk for <span className="text-lime-300 font-bold">support@playgroundleague.pro</span>
          </p>
        </div>

        {submitted ? (
          <div className="p-6 bg-lime-400/20 border border-lime-400/40 rounded-2xl text-center space-y-3">
            <CheckCircle2 className="w-12 h-12 text-lime-400 mx-auto animate-bounce" />
            <div className="space-y-1">
              <h4 className="font-extrabold text-lime-300 text-base">Support Ticket Submitted!</h4>
              <p className="text-xs text-indigo-100/90 font-medium leading-relaxed">
                Thank you, {name || 'Athlete'}. Your ticket has been logged and routed to <span className="text-lime-300 font-bold">support@playgroundleague.pro</span>.
              </p>
            </div>
            <div className="p-3 bg-indigo-950/80 rounded-xl border border-white/10 text-xs text-indigo-200 text-left space-y-1 font-mono">
              <p><strong className="text-indigo-100">Ticket Ref:</strong> #PL-{Math.floor(100000 + Math.random() * 900000)}</p>
              <p><strong className="text-indigo-100">Sender:</strong> {email}</p>
              <p><strong className="text-indigo-100">Recipient:</strong> support@playgroundleague.pro</p>
            </div>
            <button
              onClick={onClose}
              className="w-full py-3 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase text-xs rounded-xl transition shadow-md"
            >
              Close Window
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-indigo-200 block mb-1">Your Name</label>
                <input
                  type="text"
                  placeholder="e.g. Marcus Smart"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-indigo-200 block mb-1">Your Email *</label>
                <input
                  type="email"
                  required
                  placeholder="athlete@domain.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">Topic / Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as any)}
                className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
              >
                <option value="reset_password">Password Reset Request</option>
                <option value="email_confirmation">Email Verification & Confirmation</option>
                <option value="oauth_domain">OAuth Domain Authorization (Apple/Google/FB)</option>
                <option value="account_issue">Account & Profile Recovery</option>
                <option value="other">General Inquiries / Feedback</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">Subject</label>
              <input
                type="text"
                placeholder="e.g. Assistance needed with GoDaddy email confirmation"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">Message Details *</label>
              <textarea
                required
                rows={3}
                placeholder="Please describe the issue or question..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full p-2.5 bg-indigo-950 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400 resize-none"
              />
            </div>

            <div className="p-3 bg-indigo-950/80 rounded-2xl border border-white/10 text-[11px] text-indigo-200/90 flex items-start space-x-2">
              <Mail className="w-4 h-4 text-lime-400 shrink-0 mt-0.5" />
              <span>
                Your message will be sent directly to <strong>support@playgroundleague.pro</strong>. Our support desk responds to athlete inquiries within 24 hours.
              </span>
            </div>

            <button
              type="submit"
              disabled={isSending}
              className="w-full py-3.5 bg-lime-400 hover:bg-lime-300 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/20 flex items-center justify-center space-x-2 transition"
            >
              {isSending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-black" />
                  <span>Submitting Ticket...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 stroke-[2.5]" />
                  <span>Send Ticket to support@playgroundleague.pro</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
